import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Hash,
  Loader2,
  Send,
  FileText,
  Download,
  Wallet,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import Pill from "@/components/Pill";
import FileUpload from "@/components/FileUpload";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefDataContext";
import { can, isResourceReadOnly, useEnabledModules } from "@/lib/permissions";
import { toast } from "sonner";
import RaiseOPADialog from "@/components/RaiseOPADialog";
import OPAStatusTiles from "@/components/OPAStatusTiles";
import useMyWallet from "@/hooks/useMyWallet";
import ReplyAssignControls from "@/components/ReplyAssignControls";
import ReplyAssignTimelineBadges from "@/components/ReplyAssignTimelineBadges";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleString();
}

function initials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";
}

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const ref = useRefData();

  const [task, setTask] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opaTarget, setOpaTarget] = useState(null);
  const [opaRefreshKey, setOpaRefreshKey] = useState(0);
  const { wallet: myWallet } = useMyWallet();

  const [message, setMessage] = useState("");
  const [statusChange, setStatusChange] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [forwardTo, setForwardTo] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [opaSummaryOpen, setOpaSummaryOpen] = useState(false);

  const moduleMeta = useEnabledModules();
  const canReply = !isResourceReadOnly(moduleMeta, "tasks") && can(user, "tasks", "update");

  const load = useCallback(async () => {
    try {
      const [t, r] = await Promise.all([
        api.get(`/tasks/${id}`),
        api.get(`/tasks/${id}/replies`),
      ]);
      setTask(t.data);
      setReplies(r.data);
    } catch (e) {
      toast.error("Task not found");
      navigate("/assignments");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!message.trim()) {
      toast.error("Message is required");
      return;
    }
    setSending(true);
    try {
      await api.post(`/tasks/${id}/replies`, {
        message: message.trim(),
        status_change: statusChange || null,
        assigned_user_id: assignTo || null,
        team_id: forwardTo || null,
        files,
      });
      setMessage("");
      setStatusChange("");
      setAssignTo("");
      setForwardTo("");
      setFiles([]);
      toast.success("Reply added");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reply failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-sm" data-testid="task-detail-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading task…
      </div>
    );
  }
  if (!task) return null;

  const projectLabel = ref.projects?.find((p) => p.value === task.project_id)?.label;
  const teamLabel = ref.teams?.find((t) => t.value === task.team_id)?.label;
  const assigneeLabel = ref.users?.find((u) => u.value === task.assigned_user_id)?.label;
  const assetLabel = ref.assets?.find((a) => a.value === task.asset_id)?.label;
  const serviceLabel = ref.services?.find((s) => s.value === task.service_id)?.label;
  const attachments = Array.isArray(task.files) ? task.files : [];
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
  };

  return (
    <div className="space-y-8" data-testid="task-detail-page">
      {/* Back link + header cluster — tight `space-y-2` grouping so the
          breadcrumb reads as one unit (matches ticket view). */}
      <div className="space-y-2">
        <Link
          to="/assignments"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="task-detail-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Assignments
        </Link>

        <header className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-overline mb-2 flex items-center gap-2">
              <Hash className="w-3 h-3" />
              <span className="font-mono normal-case tracking-normal" data-testid="task-detail-code">
                {task.code}
              </span>
            </div>
            <h1
              className="text-3xl font-semibold tracking-tight text-white"
              style={{ fontFamily: "Outfit" }}
              data-testid="task-detail-subject"
            >
              {task.subject}
            </h1>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <Pill kind="priority" value={task.priority} testid="task-detail-priority" />
              <Pill kind="status" value={task.status} testid="task-detail-status" />
              {projectLabel && (
                <Link
                  to={`/projects/${task.project_id}`}
                  className="text-xs text-sky-400 hover:underline"
                >
                  CDARK: {projectLabel}
                </Link>
              )}
              {teamLabel && (
                <span className="text-xs text-zinc-500">Team: {teamLabel}</span>
              )}
              {assigneeLabel && (
                <span className="text-xs text-zinc-500">Assigned: {assigneeLabel}</span>
              )}
              {task.due_date && (
                <span className="text-xs text-zinc-500">Due: {task.due_date}</span>
              )}
              <span className="text-xs text-zinc-500">Created: {fmtDate(task.created_at)}</span>
              <button
                type="button"
                onClick={() => setOpaSummaryOpen(true)}
                className="ml-auto inline-flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 hover:underline"
                data-testid="task-detail-opa-summary-link"
              >
                <Wallet className="w-3 h-3" />
                OPA Summary
              </button>
            </div>
          </div>
          <Button
            onClick={() => {
              if (!myWallet) {
                toast.error("No wallet tagged to your account. Contact your manager to enable Raise OPA.");
                return;
              }
              setOpaTarget({ type: "task", id: task.id, code: task.code, subject: task.subject });
            }}
            disabled={!myWallet}
            className="bg-emerald-500 text-black hover:bg-emerald-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="task-detail-raise-opa"
            title={myWallet ? "Raise an OPA against this task" : "No wallet tagged — contact your manager"}
          >
            <Wallet className="w-4 h-4 mr-1.5" /> Raise OPA
          </Button>
        </header>
      </div>

      <RaiseOPADialog target={opaTarget} onClose={() => setOpaTarget(null)} onCreated={() => setOpaRefreshKey((k) => k + 1)} />

      {/* OPA Summary lightbox — trigger lives in the header metadata row. */}
      <Dialog open={opaSummaryOpen} onOpenChange={setOpaSummaryOpen}>
        <DialogContent
          className="max-w-3xl bg-zinc-950 border-white/10 text-zinc-100"
          data-testid="task-detail-opa-summary-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white">OPA Summary</DialogTitle>
          </DialogHeader>
          <OPAStatusTiles taskId={task.id} refreshKey={opaRefreshKey} />
        </DialogContent>
      </Dialog>

      {(task.details || assetLabel || serviceLabel || attachments.length > 0) && (
        <section
          className="bg-zinc-900 border border-white/10 rounded-lg p-5"
          data-testid="task-detail-details"
        >
          {/* Header row: overline + inline Related Asset / Service / Attachments. */}
          <div className="flex items-center gap-4 flex-wrap mb-3">
            <div className="label-overline">Task Summary</div>
            {assetLabel && (
              <Link
                to={`/assets/${task.asset_id}`}
                className="text-xs text-sky-400 hover:underline inline-flex items-center gap-1"
                data-testid="task-detail-related-asset"
              >
                <span className="text-zinc-500">Related Asset:</span>
                <span className="truncate max-w-[220px]">{assetLabel}</span>
              </Link>
            )}
            {serviceLabel && (
              <Link
                to={`/services/${task.service_id}`}
                className="text-xs text-sky-400 hover:underline inline-flex items-center gap-1"
                data-testid="task-detail-related-service"
              >
                <span className="text-zinc-500">Related Service:</span>
                <span className="truncate max-w-[220px]">{serviceLabel}</span>
              </Link>
            )}
            {attachments.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-xs text-sky-400 hover:underline inline-flex items-center gap-1"
                    data-testid="task-detail-attachments-link"
                  >
                    <FileText className="w-3 h-3" />
                    Attachments ({attachments.length})
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-80 bg-zinc-900 border-white/10 text-zinc-100 p-2"
                  data-testid="task-detail-attachments-popover"
                >
                  <div className="label-overline px-1 mb-2">Attachments</div>
                  <ul className="space-y-1">
                    {attachments.map((f) => (
                      <li
                        key={f.id}
                        className="flex items-center justify-between rounded-md bg-zinc-950 border border-white/5 px-2 py-1.5 text-xs"
                      >
                        <span className="flex items-center gap-1.5 text-zinc-200 truncate">
                          <FileText className="w-3.5 h-3.5 text-zinc-500" />
                          <span className="truncate">{f.name || f.filename}</span>
                        </span>
                        <a
                          href={`${api.defaults.baseURL}/files/${f.id}/download?auth=${encodeURIComponent(localStorage.getItem("ops_token") || "")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sky-400 hover:underline inline-flex items-center gap-1 ml-2 shrink-0"
                          data-testid={`task-detail-attachment-download-${f.id}`}
                        >
                          <Download className="w-3 h-3" /> Get
                        </a>
                      </li>
                    ))}
                  </ul>
                </PopoverContent>
              </Popover>
            )}
          </div>
          {task.details && (
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{task.details}</p>
          )}
        </section>
      )}

      {/* Timeline — placed above the reply form so users see prior context
          before composing (matches ticket layout). */}
      <section data-testid="task-timeline">
        <div className="label-overline mb-4">Reply timeline ({replies.length})</div>
        {replies.length === 0 ? (
          <div className="bg-zinc-900 border border-white/10 rounded-lg p-8 text-center text-sm text-zinc-500">
            No replies yet.
          </div>
        ) : (
          <ol className="relative border-l border-white/10 ml-3 space-y-6">
            {replies.map((r) => (
              <li key={r.id} className="ml-6" data-testid={`task-reply-${r.id}`}>
                <span className="absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full bg-zinc-900 border border-white/10 text-[10px] font-medium text-white">
                  {initials(r.user_name || r.user_email)}
                </span>
                <div className="bg-zinc-900 border border-white/10 rounded-lg p-4">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                    <div className="text-sm">
                      <span className="font-medium text-white">
                        {r.user_name || r.user_email}
                      </span>
                      {r.user_email && r.user_name && (
                        <span className="text-xs text-zinc-500 ml-2">
                          ({r.user_email})
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500" title={r.created_at}>
                      {timeAgo(r.created_at)}
                    </div>
                  </div>
                  {r.status_change ? (
                    <div className="text-xs text-zinc-400 mb-2 flex items-center gap-2">
                      Changed status:
                      {r.previous_status && (
                        <Pill kind="status" value={r.previous_status} />
                      )}
                      <span className="text-zinc-500">→</span>
                      <Pill kind="status" value={r.status_change} />
                    </div>
                  ) : (
                    <div className="mb-2">
                      <Pill kind="status" value={r.current_status || task.status} />
                    </div>
                  )}
                  <ReplyAssignTimelineBadges reply={r} referenceData={ref} />
                  <p className="text-sm text-zinc-200 whitespace-pre-wrap">
                    {r.message}
                  </p>
                  {Array.isArray(r.files) && r.files.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {r.files.map((f) => (
                        <a
                          key={f.id}
                          href={`${api.defaults.baseURL}/files/${f.id}/download?auth=${encodeURIComponent(localStorage.getItem("ops_token") || "")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-sky-400 hover:underline bg-zinc-950 border border-white/10 rounded-md px-2 py-1"
                          data-testid={`task-reply-file-${f.id}`}
                        >
                          <FileText className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[180px]">{f.filename}</span>
                          <Download className="w-3 h-3 opacity-60" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Reply form */}
      {canReply ? (
        <section
          className="bg-zinc-900 border border-white/10 rounded-lg p-5 space-y-4"
          data-testid="task-reply-form"
        >
          <div className="label-overline">Add reply</div>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write an update…"
            className="bg-zinc-950 border-white/10 text-white min-h-[100px]"
            data-testid="task-reply-message"
          />
          <FileUpload value={files} onChange={setFiles} testid="task-reply-files" />
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-zinc-400">Change status</Label>
                <Select value={statusChange} onValueChange={setStatusChange}>
                  <SelectTrigger
                    className="bg-zinc-950 border-white/10 text-white w-44"
                    data-testid="task-reply-status"
                  >
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                    {(ref.statuses || []).map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {statusChange && (
                  <button
                    type="button"
                    onClick={() => setStatusChange("")}
                    className="text-xs text-zinc-500 hover:text-white"
                  >
                    Clear
                  </button>
                )}
              </div>
              <ReplyAssignControls
                assignTo={assignTo}
                setAssignTo={setAssignTo}
                forwardTo={forwardTo}
                setForwardTo={setForwardTo}
                referenceData={ref}
                kind="task"
              />
            </div>
            <Button
              onClick={submit}
              disabled={sending}
              className="bg-white text-black hover:bg-zinc-200 font-medium"
              data-testid="task-reply-submit"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {sending ? "Posting…" : "Post reply"}
            </Button>
          </div>
        </section>
      ) : (
        <div className="text-xs text-zinc-500 italic">
          You have read-only access to tasks. Ask an admin if you need to reply.
        </div>
      )}
    </div>
  );
}
